
import React, { useState, useEffect } from 'react';
import { NAV_ITEMS } from '../constants.tsx';
import { Menu, X, ArrowRight } from 'lucide-react';

interface NavbarProps {
  onNavigate: (page: string) => void;
  activePage: string;
}

const Navbar: React.FC<NavbarProps> = ({ onNavigate, activePage }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLinkClick = (e: React.MouseEvent, href: string) => {
    e.preventDefault();
    const page = href.replace('#', '');
    onNavigate(page || 'home');
    setIsMobileMenuOpen(false);
  };

  return (
    <div className={`fixed top-0 left-0 w-full z-[100] transition-all duration-700 pointer-events-none flex justify-center ${isScrolled ? 'pt-6' : 'pt-10'}`}>
      <nav className={`pointer-events-auto transition-all duration-700 glass-island rounded-2xl px-8 py-4 flex items-center justify-between shadow-2xl ${isScrolled ? 'w-[90%] lg:w-[800px]' : 'w-[95%] lg:w-[1100px]'}`}>
        <a 
          href="#" 
          onClick={(e) => handleLinkClick(e, '#')}
          className="text-xl font-bold tracking-tighter flex items-center group"
        >
          <span className="text-white">Boulai</span>
          <span className="text-brand-blue group-hover:scale-150 transition-transform">.</span>
        </a>

        <div className="hidden lg:flex items-center space-x-12">
          {NAV_ITEMS.map((item) => (
            <a 
              key={item.label} 
              href={item.href} 
              onClick={(e) => handleLinkClick(e, item.href)}
              className={`text-[9px] uppercase tracking-[0.3em] font-black transition-all duration-300
                ${activePage === item.href.replace('#', '') ? 'text-brand-blue' : 'text-white/30 hover:text-white'}
              `}
            >
              {item.label}
            </a>
          ))}
          <div className="h-5 w-px bg-white/10"></div>
          <a 
            href="mailto:mdahhan@boulai.net" 
            className="group flex items-center gap-3 text-[9px] font-black uppercase tracking-[0.3em] text-white hover:text-brand-blue transition-colors"
          >
            <span>Request Demo</span>
            <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </a>
        </div>

        <button className="lg:hidden text-white" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {isMobileMenuOpen && (
        <div className="fixed inset-0 bg-brand-black/98 backdrop-blur-2xl z-[110] flex flex-col items-center justify-center space-y-12 p-10 lg:hidden animate-in fade-in duration-500 pointer-events-auto">
          <button className="absolute top-10 right-10 text-white" onClick={() => setIsMobileMenuOpen(false)}>
            <X size={40} />
          </button>
          {NAV_ITEMS.map((item) => (
            <a 
              key={item.label} 
              href={item.href} 
              className="text-4xl font-black uppercase tracking-tighter hover:text-brand-blue transition-colors"
              onClick={(e) => handleLinkClick(e, item.href)}
            >
              {item.label}
            </a>
          ))}
          <a href="mailto:mdahhan@boulai.net" className="mt-12 px-12 py-6 bg-brand-blue text-white font-black rounded-xl uppercase tracking-widest text-xs shadow-xl shadow-brand-blue/20">
            Request Demo
          </a>
        </div>
      )}
    </div>
  );
};

export default Navbar;